import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Mail, Shield, LifeBuoy, ChevronDown } from "lucide-react";
import { Reveal, LineReveal } from "../components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  "General support",
  "Account & login",
  "License verification",
  "Listings & applications",
  "Payments & subscriptions",
  "Privacy request",
  "Report abuse",
  "Other",
];

const channels = [
  { label: "Support", value: "support@probeauty.app", desc: "Help with the app, accounts, and listings.", Icon: LifeBuoy },
  { label: "Privacy Team", value: "privacy@probeauty.app", desc: "Data access, export, deletion, and privacy rights.", Icon: Shield },
  { label: "Legal", value: "legal@probeauty.app", desc: "Terms, compliance, and legal notices.", Icon: Mail },
];

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", topic: topics[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support-messages`, {
        name: form.name,
        email: form.email,
        topic: form.topic,
        message: form.message,
      });
      setDone(true);
      toast.success("Message sent. Our team will reply within 1–2 business days.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again or email support@probeauty.app.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative z-10 pt-28 md:pt-36 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Link
          to="/"
          data-testid="back-to-legal-home"
          className="group inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] hover:text-[#C6826D] transition-colors"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to legal home
        </Link>

        <div className="mt-10 md:mt-14 border-b border-[#E0E0D9] pb-12 md:pb-16">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.3em] text-[#C6826D]">
            ProBeauty Connect
          </div>
          <h1 className="mt-6 font-serif-display font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
            <LineReveal lines={["Contact &", "Support"]} />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg text-[#525252] leading-relaxed">
              Questions, feedback, or a request about your data? Send us a message
              below and the right team will get back to you — usually within 1–2
              business days.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* FORM */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-6">
            Send us a message
          </div>

          {done ? (
            <motion.div
              data-testid="support-success"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F9F6] p-8 md:p-10"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#C6826D]">
                  <Check size={18} />
                </span>
                <div className="font-serif-display text-3xl">Message sent</div>
              </div>
              <p className="mt-5 text-[#F9F9F6]/80 leading-relaxed max-w-lg">
                Thanks, <strong className="text-[#F9F9F6]">{form.name}</strong>. We've received your message
                and will reply to <strong className="text-[#F9F9F6]">{form.email}</strong> within 1–2 business days.
              </p>
              <button
                data-testid="support-send-another"
                onClick={() => { setDone(false); setForm({ name: "", email: "", topic: topics[0], message: "" }); }}
                className="mt-8 border border-[#F9F9F6]/40 px-6 py-3 text-sm hover:bg-[#F9F9F6] hover:text-[#0A0A0A] transition-colors"
              >
                Send another message
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="support-form" className="border border-[#E0E0D9] bg-[#F9F9F6] p-8 md:p-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-7">
                <div>
                  <label htmlFor="name" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                    Your name <span className="text-[#C6826D]">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    data-testid="support-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Marcus Rivera"
                    className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="s-email" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                    Email <span className="text-[#C6826D]">*</span>
                  </label>
                  <input
                    id="s-email"
                    type="email"
                    required
                    data-testid="support-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  />
                </div>
              </div>

              <div className="mb-7">
                <label htmlFor="topic" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                  Topic
                </label>
                <div className="relative">
                  <select
                    id="topic"
                    data-testid="support-input-topic"
                    value={form.topic}
                    onChange={update("topic")}
                    className="w-full appearance-none bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 pr-10 text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] transition-colors cursor-pointer"
                  >
                    {topics.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none" />
                </div>
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                  Message <span className="text-[#C6826D]">*</span>
                </label>
                <textarea
                  id="message"
                  rows={5}
                  required
                  data-testid="support-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="How can we help?"
                  className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="support-submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#F9F9F6] px-8 py-4 text-sm tracking-tight transition-colors duration-300 hover:bg-[#C6826D] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>

        {/* CHANNELS */}
        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-6">
            Prefer email?
          </div>
          <div className="space-y-4">
            {channels.map(({ label, value, desc, Icon }) => (
              <Reveal key={value}>
                <a
                  href={`mailto:${value}`}
                  data-testid={`support-channel-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex items-start gap-4 border border-[#E0E0D9] bg-[#F9F9F6] p-6 transition-colors duration-300 hover:bg-[#F3F3EF]"
                >
                  <Icon size={22} strokeWidth={1.25} className="text-[#0A0A0A] shrink-0 mt-1 transition-colors group-hover:text-[#C6826D]" />
                  <div>
                    <div className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A]">{label}</div>
                    <div className="font-serif-display text-2xl text-[#0A0A0A] mt-0.5">{value}</div>
                    <p className="text-sm text-[#525252] mt-1 leading-relaxed">{desc}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-4 border border-[#E0E0D9] bg-[#F3F3EF] p-6">
              <div className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-2">
                Security issues
              </div>
              <p className="text-sm text-[#525252] leading-relaxed">
                Report suspected unauthorized account use to{" "}
                <a href="mailto:security@probeauty.app" className="text-[#C6826D] underline">security@probeauty.app</a>.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
